import { useMemo } from 'react';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { useGetPickemByIdQuery } from '@/api/pickems/getPickemById';
import { useGetQuestionByIdQuery } from '@/api/questions/getQuestionById';
import { SPORT_TO_LEAGUE_ID } from '@low6-pickem/sports-config';
import type { QuestionData } from '@/utils/forms/questionTransformers';

interface UseQuestionFormDataProps {
  questionId: string;
  isNewQuestion: boolean;
  selectedPickemId?: string | null;
}

export function useQuestionFormData({
  questionId,
  isNewQuestion,
  selectedPickemId,
}: UseQuestionFormDataProps) {
  // When editing: fetch question by ID (1 call instead of all questions)
  // When creating: question is undefined
  const { data: question, isLoading: questionLoading } = useGetQuestionByIdQuery(
    !isNewQuestion ? questionId : undefined
  );

  // When editing: fetch pickem by ID (1 call instead of all pickems)
  // When creating: fetch all pickems for dropdown
  const { data: pickemById, isLoading: pickemByIdLoading } = useGetPickemByIdQuery(
    !isNewQuestion ? (selectedPickemId ?? undefined) : undefined
  );
  const { data: pickems = [], isLoading: pickemsLoading } = useGetPickemsQuery(undefined, {
    enabled: isNewQuestion,
  });

  // When editing, use single-item array from by-ID query for dropdown compatibility
  // When creating, use full list for dropdown selection
  const pickemsForDropdown = useMemo((): Array<{
    pickem_id: string;
    title_en?: string;
    title_fr?: string;
    sport?: string;
  }> => {
    if (!isNewQuestion && pickemById) {
      return [pickemById];
    }
    return pickems;
  }, [isNewQuestion, pickemById, pickems]);

  // Check if question is editable based on pickem status
  const isQuestionEditable = question?.editable !== false;
  const questionEditMessage = question?.edit_message;

  // Get pickem to determine sport/league for fixture selection
  const selectedPickem = useMemo(() => {
    if (!selectedPickemId) return null;
    if (!isNewQuestion) {
      return pickemById ?? null;
    }
    return pickems.find(p => p.pickem_id === selectedPickemId) ?? null;
  }, [isNewQuestion, pickemById, pickems, selectedPickemId]);

  const sport = selectedPickem?.sport;
  const leagueId = sport ? SPORT_TO_LEAGUE_ID[sport] : undefined;
  // League IP (Intellectual Property) flag — decides whether the option image
  // auto-fill bakes licensed imagery (logos/headshots) or generic art
  // (avatars/jerseys). Defaults off when the pickem isn't loaded yet.
  const leagueIp =
    (selectedPickem as { league_ip?: boolean } | null | undefined)?.league_ip ?? false;

  const isLoading = isNewQuestion ? pickemsLoading : questionLoading || pickemByIdLoading;

  return {
    pickems: pickemsForDropdown,
    question: question as QuestionData | undefined,
    isLoading,
    isQuestionEditable,
    questionEditMessage,
    selectedPickem,
    sport,
    leagueId,
    leagueIp,
  };
}
